import React from "react"
import * as DateFns from "date-fns"
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid"

import { Idea, Project } from "../../../../model/types"
import Model from "../../../../model/model"
import IdeaBox from "../../../components/idea_box"
import GoalAutocomplete from "../../../components/goal_autocomplete"

export type ProjectFilterType = {
  query: string
  goal: null | Idea | "none"
  sortOrder: {
    in_progress: SortOrder
    completed: SortOrder
  }
}

export type ProjectListsType = {
  in_progress: Project[]
  completed: Project[]
}

export type SortOrder = "oldest-first" | "newest-first"

export const filterProjects = (
  model: Model,
  newFilter: ProjectFilterType
): ProjectListsType => {
  const projects = model
    .getProjects()
    .filter((project: Project) => {
      return (
        newFilter.query === "" ||
        (project.name &&
          project.name.toLowerCase().includes(newFilter.query.toLowerCase()))
      )
    })
    .filter((project: Project) => {
      return (
        !newFilter.goal ||
        (newFilter.goal === "none" && project.goalId === null) ||
        (newFilter.goal !== "none" && project.goalId === newFilter.goal.id)
      )
    })

  return {
    in_progress: projects
      .filter((project: Project) => project.status == "in_progress")
      .sort((a: Project, b: Project) => {
        if (newFilter.sortOrder.in_progress == "newest-first")
          return DateFns.compareAsc(b.updatedAt, a.updatedAt)
        return DateFns.compareAsc(a.updatedAt, b.updatedAt)
      }),
    completed: projects
      .filter((project: Project) => project.status == "completed")
      .sort((a: Project, b: Project) => {
        if (newFilter.sortOrder.completed == "newest-first")
          return DateFns.compareAsc(
            b.completedAt || b.updatedAt,
            a.completedAt || a.updatedAt
          )
        return DateFns.compareAsc(
          a.completedAt || a.updatedAt,
          b.completedAt || b.updatedAt
        )
      }),
  }
}

export default function ProjectFilter({ filter, setFilter }) {
  return (
    <div id="project-filter" className="flex flex-col">
      <div className="relative z-0 flex flex-1 items-center justify-center">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search projects"
          name="filter"
          value={filter.query}
          onChange={(e) => {
            const newFilter: ProjectFilterType = {
              ...filter,
              query: e.target.value,
            }
            setFilter(newFilter)
          }}
          className="block w-full rounded-md border-0 bg-white dark:bg-gray-800 py-1.5 pl-10 pr-3 text-gray-900 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
        {filter.query != "" && (
          <div
            className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer z-10"
            onClick={() => {
              const newFilter: ProjectFilterType = { ...filter, query: "" }
              setFilter(newFilter)
            }}
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </div>
      <div className="mt-2 flex gap-3">
        {filter.goal && filter.goal !== "none" && (
          <div className="min-w-0 flex justify-start items-center gap-2 ">
            <IdeaBox idea={filter.goal} />
            <div className="text-sm text-gray-600 dark:text-gray-100 overflow-x-hidden whitespace-nowrap text-ellipsis">
              {filter.goal.title}
            </div>
            <div
              className="cursor-pointer text-gray-600 hover:text-gray-400"
              onClick={() => {
                const newFilter: ProjectFilterType = { ...filter, goal: null }
                setFilter(newFilter)
              }}
            >
              <XMarkIcon className="w-4 h-4" />
            </div>
          </div>
        )}
        {filter.goal && filter.goal === "none" && (
          <div className="flex justify-between items-center gap-2 min-w-0 overflow-x-hidden">
            <div className="text-sm text-gray-600 dark:text-gray-100 overflow-hidden whitespace-nowrap text-ellipsis">
              No goal
            </div>
            <div
              className="cursor-pointer text-gray-600 hover:text-gray-400"
              onClick={() => {
                const newFilter: ProjectFilterType = { ...filter, goal: null }
                setFilter(newFilter)
              }}
            >
              <XMarkIcon className="w-4 h-4" />
            </div>
          </div>
        )}
        {!filter.goal && (
          <div className="flex-grow">
            <GoalAutocomplete
              canSelectNone={true}
              placeholder="Filter by goal"
              select={(result, _filter) => {
                if (result.type == "item") {
                  const goal = result.item
                  const newFilter: ProjectFilterType = { ...filter, goal }
                  setFilter(newFilter)
                } else if (result.type == "none") {
                  const newFilter: ProjectFilterType = {
                    ...filter,
                    goal: "none",
                  }
                  setFilter(newFilter)
                }
                return true
              }}
              onEscape={() => {}}
              inputStyle="inset"
            />
          </div>
        )}
      </div>
    </div>
  )
}
